<template>
    <BaseDialog v-model:open="open" title="Manage Sources" show-close dialog-content-class="md:w-160">
        <div class="p-6 pt-0 min-w-90 md:w-160 w-full flex flex-col gap-4">
            <div class="flex flex-col gap-4">
                <ProfileSourcesHeader />
                <FreeTextUpload v-model:free-text="freeText" :free-text-enabled="freeTextEnabled" />

                <template v-if="documentUploadEnabled">
                    <ProfileDocumentsHeading />
                    <ProfileFileUploadArea :accepted-file-types="acceptedFileTypes" :is-uploading="isUploading" @file-upload="handleFileUpload" />
                    <div class="flex flex-col max-h-48 overflow-y-auto gap-2">
                        <FileAttachedTag
                            v-for="file in uploadedFiles"
                            :key="file.name"
                            :file="file"
                            is-removable
                            data-testid="profile-source-file-attached-tag"
                            @remove="removeFile(file.id)" />
                    </div>
                    <BaseNotification v-if="fileUploadError" theme="error" :text="fileUploadError" @dismiss="fileUploadError = ''" />
                </template>

                <div class="flex justify-between gap-2 pt-2">
                    <BaseButton theme="tertiary" :disabled="isUploading" @click="open = false">Cancel</BaseButton>
                    <BaseTooltip side="top" :disabled="hasSource">
                        <template #trigger>
                            <!-- A disabled button emits no pointer events, so the wrapper carries the hover that opens the tooltip. -->
                            <span>
                                <BaseButton theme="primary" :disabled="isUploading || !hasSource" data-testid="profile-create-from-sources" @click="handleSave">
                                    Create Profile
                                </BaseButton>
                            </span>
                        </template>
                        <template #content>
                            <span class="body-small">{{
                                documentUploadEnabled ? "Describe how you work or add a document to get started." : "Describe how you work to get started."
                            }}</span>
                        </template>
                    </BaseTooltip>
                </div>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import FreeTextUpload from "~vue/CollaborationProfile/FreeTextUpload.vue";
import ProfileDocumentsHeading from "~vue/CollaborationProfile/ProfileDocumentsHeading.vue";
import ProfileFileUploadArea from "~vue/CollaborationProfile/ProfileFileUploadArea.vue";
import ProfileSourcesHeader from "~vue/CollaborationProfile/ProfileSourcesHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import { computed, ref } from "vue";

import FileAttachedTag from "../FileAttachedTag.vue";

const { documentUploadEnabled = true, freeTextEnabled = false } = defineProps<{ documentUploadEnabled?: boolean; freeTextEnabled?: boolean }>();

const emit = defineEmits<{ createProfile: [fileIds: number[], freeText: string] }>();

const open = defineModel<boolean>("open", { default: false });

const { uploadedFiles, acceptedFileTypes, handleFileUpload, isUploading, fileUploadError, removeFile } = useProfileSources();

const freeText = ref<string>("");

const hasSource = computed(() => !freeTextEnabled || !!freeText.value.trim() || uploadedFiles.value.length > 0);

const handleSave = () => {
    emit(
        "createProfile",
        uploadedFiles.value.map((file) => file.id),
        freeText.value.trim(),
    );
    open.value = false;
};
</script>
